import React, { useState, lazy } from 'react';
import { ImageData } from '../utils/mockData';
import { SignificanceFlow } from './SignificanceFlow';
import { MiniDonutChart } from './MiniDonutChart';
import { FloatingLegend } from './FloatingLegend';
import { BayesianDistributionChart } from './BayesianDistributionChart';
interface StatisticalMatrixProps {
  data: ImageData[];
}
export const StatisticalMatrix: React.FC<StatisticalMatrixProps> = ({
  data
}) => {
  const [currentSignificance, setCurrentSignificance] = useState(95);
  const sortedData = [...data].sort((a, b) => b.stoppingPower - a.stoppingPower);
  return <div className="space-y-8" data-id="element-325" data-mp="3sik2twmyu3ap">
      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5]" data-id="element-326" data-mp="3shifasorjoc3">
        <h3 className="text-lg font-medium text-[#141617] mb-6" data-id="element-327" data-mp="3oh8ahjcpj3yt">
          Explanation of Tests
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-328" data-mp="1y63oe8k0niur">
          <div className="p-4 bg-[#F9F9FB] rounded-sm border border-[#F2F3F5]" data-id="element-329" data-mp="1u3h3krlriy72">
            <h4 className="font-medium text-[#141617] mb-3 flex items-center" data-id="element-330" data-mp="2to8knjfvx2ya">
              <span className="w-6 h-6 rounded-sm bg-[#D1005D] text-white flex items-center justify-center text-sm mr-2" data-id="element-331" data-mp="2ymtff3cdaufc">
                1
              </span>
              Chi-Square Test
            </h4>
            <p className="text-sm text-[#212326]" data-id="element-332" data-mp="38ccz2b7wmsb9">
              Tests whether the differences in engagement behaviour across all
              assets are statistically significant.
              <br data-id="element-333" data-mp="3hdb2araiqvss" />
              <br data-id="element-334" data-mp="3hcrva5meprtj" />
              <span className="font-medium text-[#D1005D]" data-id="element-335" data-mp="3hc9r8mti69b2">Result:</span>{' '}
              Significant at 95% confidence (p = 0.018)
            </p>
          </div>
          <div className="p-4 bg-[#F9F9FB] rounded-sm border border-[#F2F3F5]" data-id="element-336" data-mp="3rbh6jfjsse0t">
            <h4 className="font-medium text-[#141617] mb-3 flex items-center" data-id="element-337" data-mp="3r94o71a4o1n8">
              <span className="w-6 h-6 rounded-sm bg-[#D1005D] text-white flex items-center justify-center text-sm mr-2" data-id="element-338" data-mp="1s07l8hw2h0to">
                2
              </span>
              Z-Test
            </h4>
            <p className="text-sm text-[#212326]" data-id="element-339" data-mp="1ox1sk9xgii7o">
              Compares the top two performing assets (Red Background vs Yellow
              Background) directly.
              <br data-id="element-340" data-mp="1yv7q6fc8d26q" />
              <br data-id="element-341" data-mp="1yvr3fiqxr2ef" />
              <span className="font-medium text-[#D1005D]" data-id="element-342" data-mp="1ysv1ljbyvtkp">Result:</span>{' '}
              Significant at 90% confidence (p = 0.084)
            </p>
          </div>
          <div className="p-4 bg-[#F9F9FB] rounded-sm border border-[#F2F3F5]" data-id="element-343" data-mp="27ukteq9rguj0">
            <h4 className="font-medium text-[#141617] mb-3 flex items-center" data-id="element-344" data-mp="23ukg58yeepdj">
              <span className="w-6 h-6 rounded-sm bg-[#D1005D] text-white flex items-center justify-center text-sm mr-2" data-id="element-345" data-mp="2hlfomk4zj4gl">
                3
              </span>
              Bayesian Test
            </h4>
            <p className="text-sm text-[#212326]" data-id="element-346" data-mp="2rjdd98qrb39q">
              Estimates the probability that each asset is truly the best
              performer.
              <br data-id="element-347" data-mp="2mm3hlbcrxvdg" />
              <br data-id="element-348" data-mp="2mifogdb8raz9" />
              <span className="font-medium text-[#D1005D]" data-id="element-349" data-mp="366c0ssmhtdj6">Result:</span> Red
              Background has an 88.5% probability of being the best.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5] overflow-x-auto" data-id="element-350" data-mp="2w8eguhbo22wd">
        <h3 className="text-lg font-medium text-[#141617] mb-6" data-id="element-351" data-mp="316fycxtg1u5q">
          Statistical Significance Matrix
        </h3>
        <SignificanceFlow significanceLevels={[95, 90, 85]} data-id="element-352" data-mp="2mhjjagsyzgvm" />
        <table className="min-w-full" data-id="element-353" data-mp="2r9l1skgqe3u3">
          <thead data-id="element-354" data-mp="2ramd8q1nsscc">
            <tr data-id="element-355" data-mp="2rb5wca31pmry">
              <th className="px-4 py-2 text-left text-xs font-medium text-[#707070] uppercase tracking-wider w-64" data-id="element-356" data-mp="2dhmzashiz66w">
                Image Asset
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-[#707070] uppercase tracking-wider" data-id="element-357" data-mp="2ih9ayxhpgedl">
                <div className="mb-2 text-[#D1005D]" data-id="element-358" data-mp="23k8slklelvrs">95% Confidence Level</div>
                Chi-Square Test
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-[#707070] uppercase tracking-wider" data-id="element-359" data-mp="3bthevxt90e7c">
                <div className="mb-2 text-[#D1005D]" data-id="element-360" data-mp="36turwwnmbjlc">95% Confidence Level</div>
                Z-Test
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-[#707070] uppercase tracking-wider" data-id="element-361" data-mp="322mhpyegru74">
                Bayesian Best Probability
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-362" data-mp="2s6jesqbsfrj4">
            {sortedData.map((item, index) => <tr key={item.id} className={`hover:bg-gray-50 transition-colors ${index === 0 ? 'bg-[#D1005D]/5' : ''}`} onMouseEnter={() => {
            if (index === 0) setCurrentSignificance(95);else if (index === 1) setCurrentSignificance(90);else setCurrentSignificance(85);
          }} data-id="element-363" data-mp="2o2bquvd5mfly" data-mpi={index}>
                <td className="px-4 py-3" data-id="element-364" data-mp="cevp9fz3t1ac">
                  <div className="flex items-center space-x-3" data-id="element-365" data-mp="jy4uqy4hofu4">
                    <div className="h-12 w-12 flex-shrink-0 rounded-sm overflow-hidden bg-[#F9F9FB]" data-id="element-366" data-mp="jxlfjp6845js">
                      <img src={item.thumbnailUrl} alt={item.name} className="h-full w-full object-cover" referrerPolicy="no-referrer" loading="lazy" data-id="element-367" data-mp="ouvb7mk7hdg2" />
                    </div>
                    <span className="text-sm font-medium text-[#141617]" data-id="element-368" data-mp="5y3vto7sbiha">
                      {item.name}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm" data-id="element-369" data-mp="az9oxn5cx7rc">
                  <span className="text-[#D1005D] font-medium" data-id="element-370" data-mp="fwjmjq2wgpq9">p = 0.018</span>
                </td>
                <td className="px-4 py-3 text-sm" data-id="element-371" data-mp="1md52irgm58gg">
                  {item.id === 1 || item.id === 2 ? <span className="text-[#D1005D] font-medium" data-id="element-372" data-mp="1radq57kj64p3">
                      p = 0.084
                    </span> : <span className="text-[#707070]" data-id="element-373" data-mp="3ngeeq9bnz5mi">N/A</span>}
                </td>
                <td className={`px-4 py-3 text-sm ${item.id === 1 ? 'text-[#D1005D] font-medium' : 'text-[#707070]'}`} data-id="element-374" data-mp="3ihumxsae2zm8">
                  <MiniDonutChart percentage={item.id === 1 ? 88.5 : item.id === 2 ? 11.2 : item.id === 3 ? 0.2 : 0.1} data-id="element-375" data-mp="3sezd037cvf8b" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      <BayesianDistributionChart data={data} data-id="element-376" data-mp="2kzunkgil7lu6" />
      <FloatingLegend data-id="element-377" data-mp="2kyruq31pf9bm" />
    </div>;
};